import React from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { CalendarItem } from "../../features/calendar/models/CalendarItem";
import { ClosedDay } from "../../features/closed-days/models/ClosedDay";
import { AvailableTimeSlot } from "../../features/sessions/time-slot/models/AvailableTimeSlot";
import { OccurrenceDrag, occurrenceKey } from "../../features/calendar/models/OccurrenceDrag";
import {
  getWeekDays,
  groupItemsByDate,
  isSameDay,
  toYmd,
  WeekStartDay,
} from "../../features/calendar/utils/calendar-range";
import CalendarItemChip from "./CalendarItemChip";
import CalendarDayMenu from "./CalendarDayMenu";
import CalendarFreeSlots from "./CalendarFreeSlots";

interface CalendarWeekViewProps {
  anchor: Date;
  items: CalendarItem[];
  closedByDate: Map<string, ClosedDay>;
  /** Free time in the visible range; empty when the range is outside the slot horizon. */
  freeSlots: AvailableTimeSlot[];
  drag: OccurrenceDrag;
  onManageDay: (day: Date, closed?: ClosedDay) => void;
  onCreateSession: (day: Date, time?: string, duration?: number) => void;
  weekStart: WeekStartDay;
}

/**
 * Seven day columns, week-start first (instructor preference). On small screens
 * the columns stack into a vertical day list (the natural "day view" on a phone).
 */
const CalendarWeekView: React.FC<CalendarWeekViewProps> = ({
  anchor,
  items,
  closedByDate,
  freeSlots,
  drag,
  onManageDay,
  onCreateSession,
  weekStart,
}) => {
  const { t, i18n } = useTranslation();
  const days = getWeekDays(anchor, weekStart);
  const byDate = groupItemsByDate(items);
  const today = new Date();
  const weekdayFormat = new Intl.DateTimeFormat(i18n.language, { weekday: "short" });
  // With nothing scheduled all week the empty-period card below carries the
  // message; repeating "No sessions" seven times just adds noise.
  const rangeEmpty = items.length === 0;

  return (
    <div className="with-border grid grid-cols-1 overflow-hidden rounded-2xl lg:grid-cols-7">
      {days.map((day) => {
        const ymd = toYmd(day);
        const dayItems = byDate.get(ymd) ?? [];
        const closed = closedByDate.get(ymd);
        const isToday = isSameDay(day, today);
        const isDropTarget = drag.dragOverYmd === ymd;
        return (
          <div
            key={ymd}
            onDragOver={(event) => drag.handleDayDragOver(day, event)}
            onDragLeave={() => drag.handleDayDragLeave(day)}
            onDrop={(event) => drag.handleDayDrop(day, event)}
            className={`group/day flex min-h-24 flex-col border-b border-grey-70 p-2 last:border-b-0 dark:border-grey-50 lg:min-h-96 lg:border-r lg:border-b-0 lg:last:border-r-0 ${
              closed ? "bg-grey-95 dark:bg-primary-20" : ""
            } ${isDropTarget ? "bg-primary-95 dark:bg-[#3E334D]" : ""}`}
          >
            <CalendarDayMenu
              day={day}
              closed={closed}
              onManageDay={onManageDay}
              onCreateSession={onCreateSession}
              triggerClassName="mb-2 text-sm"
            >
              <span className="text-grey-40 dark:text-grey-70">{weekdayFormat.format(day)}</span>
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full font-semibold ${
                  isToday ? "bg-primary-40 text-white" : "text-grey-20 dark:text-white"
                }`}
              >
                {day.getDate()}
              </span>
              {closed && (
                <span className="ms-auto rounded-full bg-grey-80 px-2 py-0.5 text-xs font-medium text-grey-30 dark:bg-primary-30 dark:text-grey-70">
                  {t("calendar.closedDay.badge")}
                </span>
              )}
            </CalendarDayMenu>

            <div className="space-y-1">
              {dayItems.map((item, index) => (
                <CalendarItemChip
                  key={`${item.bookingId}-${item.date}-${index}`}
                  item={item}
                  draggable={drag.canDrag(item)}
                  isDragging={drag.draggingKey === occurrenceKey(item)}
                  onDragStart={(event) => drag.handleDragStart(item, event)}
                  onDragEnd={drag.handleDragEnd}
                />
              ))}
              {dayItems.length === 0 && !rangeEmpty && (
                <span className="hidden text-xs text-grey-60 dark:text-grey-50 lg:block">
                  {closed ? t("calendar.closedDay.emptyClosed") : t("calendar.emptyDay")}
                </span>
              )}
            </div>

            {/* Where there is still room — tapping a slot opens the form on that
                day and time. A closed day is not offering its time. */}
            {!closed && (
              <CalendarFreeSlots
                slots={freeSlots}
                day={day}
                onPick={(pickedDay, time, duration) => onCreateSession(pickedDay, time, duration)}
              />
            )}

            {/* The rest of the column schedules onto this day — the affordance
                stays quiet until the column is hovered or focused. */}
            <button
              type="button"
              onClick={() => onCreateSession(day)}
              className="mt-1 hidden min-h-11 flex-1 items-center justify-center rounded-lg text-xs text-grey-40 opacity-0 transition-opacity hover:bg-grey-95 focus-visible:opacity-100 group-hover/day:opacity-100 dark:text-grey-60 dark:hover:bg-primary-20 lg:flex"
            >
              <Plus size={14} className="me-1" />
              <span>{t("sessions.createSession")}</span>
              {/* Seven identical buttons otherwise read the same to a screen
                  reader — the day makes each one distinguishable. */}
              <span className="sr-only"> — {weekdayFormat.format(day)} {day.getDate()}</span>
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default CalendarWeekView;
